import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Chip
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useAppMeta, useTablePagination } from "hooks";
import { useLazyGetMerchantReportQuery } from "store/services/reportApi";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

const MerchantReportListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";
  const timeFilter = searchParams.get("timeFilter") || "all";
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";

  const breadcrumbs = [{ title: "Merchant Reports" }];

  const columns = [
    {
      name: "Merchant Name",
      selector: (row) => row.merchantName,
      cell: (row) => (
        <Link
          title={row?.merchantName}
          to={`/app/crm/merchant/${row?._id}/detail?tab=transactions`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.merchantName ? (
            <HighlightedText text={row?.merchantName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Merchant Type",
      selector: (row) => row.merchantType,
      cell: (row) => row.merchantType || "N/A",
    },
    {
      name: "Total Fuel Cost",
      selector: (row) => row.totalFuelCost,
      cell: (row) => (row.totalFuelCost ? `Rs. ${row.totalFuelCost.toFixed(2)}` : "N/A"),
    },
    {
      name: "Total Maintenance Cost",
      selector: (row) => row.totalMaintenanceCost,
      cell: (row) => (row.totalMaintenanceCost ? `Rs. ${row.totalMaintenanceCost.toFixed(2)}` : "Rs. 0.00"),
    },
    {
      name: "Total Insurance Cost",
      selector: (row) => row.totalInsuranceCost,
      cell: (row) => (row.totalInsuranceCost ? `Rs. ${row.totalInsuranceCost.toFixed(2)}` : "Rs. 0.00"),
    },
    {
      name: "Total Cost",
      selector: (row) => row.totalCost,
      cell: (row) => (
        <span className="font-semibold text-green-600">
          {row.totalCost ? `Rs. ${row.totalCost.toFixed(2)}` : "Rs. 0.00"}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Link to={`/app/crm/merchant/${row._id}/detail?tab=transactions`}>
          <IconButton size="small" aria-label="View Merchant" color="primary">
            <InfoOutlined />
          </IconButton>
        </Link>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchMerchantReport, { data: merchantReportRes, isLoading, isFetching, error }] =
    useLazyGetMerchantReportQuery();

  React.useEffect(() => {
    const params = {
      page: currentPage,
      limit: perPage,
      timeFilter,
      ...(keyword && { keyword }),
    };

    // Add date range params for custom filter
    if (timeFilter === "custom") {
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
    }

    fetchMerchantReport({ params });
  }, [currentPage, fetchMerchantReport, keyword, perPage, timeFilter, startDate, endDate]);

  const handleTimeFilterChange = (event) => {
    const newTimeFilter = event.target.value;
    const newParams = { search: keyword, timeFilter: newTimeFilter };

    // Clear date params when not using custom filter
    if (newTimeFilter !== "custom") {
      setSearchParams(newParams);
    } else {
      // Keep existing dates if switching to custom
      if (startDate) newParams.startDate = startDate;
      if (endDate) newParams.endDate = endDate;
      setSearchParams(newParams);
    }
  };

  const handleDateChange = (field, date) => {
    const dateValue = date ? date.toISOString().split('T')[0] : "";

    const newParams = {
      search: keyword,
      timeFilter,
    };

    if (field === "startDate") {
      if (dateValue) newParams.startDate = dateValue;
      if (endDate) newParams.endDate = endDate;
    } else {
      if (startDate) newParams.startDate = startDate;
      if (dateValue) newParams.endDate = dateValue;
    }

    setSearchParams(newParams);
  };

  const getFilterDisplayText = () => {
    switch (timeFilter) {
      case "1week":
        return "Last Week";
      case "1month":
        return "Last Month";
      case "custom":
        if (startDate && endDate) {
          return `${startDate} to ${endDate}`;
        } else if (startDate) {
          return `From ${startDate}`;
        } else if (endDate) {
          return `Until ${endDate}`;
        }
        return "Custom Range";
      default:
        return null;
    }
  };

  useAppMeta("Merchant Reports");

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={error} />

        <BreadcrumbLayout.Paper>
          {/* Filters Section */}
          <Box className="space-y-4 mb-4">
            <div className="flex flex-wrap gap-4 items-end">
              <TableFilter
                searchLabel="Search Merchant Reports"
                disabled={isLoading || isFetching}
              />

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Time Filter</InputLabel>
                <Select
                  value={timeFilter}
                  label="Time Filter"
                  onChange={handleTimeFilterChange}
                  disabled={isLoading || isFetching}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="1week">Last Week</MenuItem>
                  <MenuItem value="1month">Last Month</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>

              {timeFilter === "custom" && (
                <>
                  <DatePicker
                    label="Start Date"
                    value={startDate ? new Date(startDate) : null}
                    onChange={(date) => handleDateChange("startDate", date)}
                    disabled={isLoading || isFetching}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ minWidth: 140 }} />
                    )}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate ? new Date(endDate) : null}
                    onChange={(date) => handleDateChange("endDate", date)}
                    disabled={isLoading || isFetching}
                    minDate={startDate ? new Date(startDate) : undefined}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ minWidth: 140 }} />
                    )}
                  />
                </>
              )}
            </div>

            {/* Active Filter Indicators */}
            {(timeFilter !== "all" || keyword) && (
              <Box className="flex gap-2 items-center flex-wrap">
                <span className="text-sm text-gray-600">Active Filters:</span>
                {keyword && (
                  <Chip
                    label={`Search: "${keyword}"`}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onDelete={() => setSearchParams({ timeFilter })}
                  />
                )}
                {timeFilter !== "all" && (
                  <Chip
                    label={getFilterDisplayText()}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onDelete={() => setSearchParams({ search: keyword, timeFilter: "all" })}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Summary Info */}
          {merchantReportRes?.summary && (
            <Box className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-sm text-gray-600">Total Fuel</div>
                  <div className="text-lg font-semibold text-blue-600">
                    Rs. {merchantReportRes.summary.totalFuelCost?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Total Maintenance</div>
                  <div className="text-lg font-semibold text-orange-600">
                    Rs. {merchantReportRes.summary.totalMaintenanceCost?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Total Insurance</div>
                  <div className="text-lg font-semibold text-purple-600">
                    Rs. {merchantReportRes.summary.totalInsuranceCost?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Grand Total</div>
                  <div className="text-xl font-bold text-green-600">
                    Rs. {merchantReportRes.summary.grandTotal?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>
            </Box>
          )}

          <AppTable
            columns={columns}
            data={merchantReportRes?.data ?? []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={merchantReportRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default MerchantReportListScreen;